import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { BRAND } from '../../constants/branding';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: BRAND.colors.text }}>
      {title}
    </Typography>
    <Typography variant="body2" component="div" sx={{ color: BRAND.colors.textDim, lineHeight: 1.9 }}>
      {children}
    </Typography>
  </Box>
);

export const PrivacyPolicyPage: React.FC = () => (
  <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
    <Typography variant="overline" sx={{ color: BRAND.colors.primary, fontWeight: 700 }}>
      Legal
    </Typography>
    <Typography variant="h3" fontWeight={800} sx={{ mt: 1, mb: 1 }}>
      Privacy Policy
    </Typography>
    <Typography variant="body2" sx={{ mb: 4, color: BRAND.colors.textDim }}>
      Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
    </Typography>

    <Section title="1. Introduction">
      {BRAND.nameUpper} (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates {BRAND.domain} and the {BRAND.name}
      application. This Privacy Policy explains how we collect, use, and protect your information when you use our services.
    </Section>

    <Section title="2. Information we collect">
      <ul>
        <li>Account information: email address, username, and profile details you provide at registration.</li>
        <li>Content data: posts, drafts, schedules, and brand settings you create in the app.</li>
        <li>Integration data: OAuth tokens and account metadata for connected social platforms.</li>
        <li>Usage data: logs, device/browser info, and analytics to improve reliability and performance.</li>
      </ul>
    </Section>

    <Section title="3. How we use your information">
      We use your data to provide and improve the service, authenticate you, send verification emails,
      publish content on your behalf to connected platforms, and respond to support requests.
    </Section>

    <Section title="4. Third-party services">
      We may use third-party providers for email delivery, hosting, analytics, and AI content generation.
      Connected social platforms receive only the content you explicitly authorize us to publish.
    </Section>

    <Section title="5. Data retention & security">
      We retain your data while your account is active and as needed to comply with legal obligations.
      We implement reasonable technical and organizational measures to protect your information.
    </Section>

    <Section title="6. Your rights">
      You may request access, correction, or deletion of your personal data by contacting{' '}
      <a href={`mailto:${BRAND.supportEmail}`} style={{ color: BRAND.colors.primary }}>
        {BRAND.supportEmail}
      </a>.
    </Section>

    <Section title="7. Contact">
      For privacy-related questions, email us at{' '}
      <a href={`mailto:${BRAND.supportEmail}`} style={{ color: BRAND.colors.primary }}>
        {BRAND.supportEmail}
      </a>.
    </Section>
  </Container>
);

export default PrivacyPolicyPage;
